import * as Sentry from "@sentry/browser";
import useScrollToTop from "core-integration/src/hooks/useScrollToTop";
import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getBootstrapped } from "core-integration/src/store/bootstrap/reducers";
import { bootstrap } from "core-integration/src/store/bootstrap/thunks";
import { getServerError } from "core-integration/src/store/global/reducers";
import { IServerError } from "core-integration/src/store/global/types";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import * as React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { ThemeProvider } from "styled-components";
import GlobalStyle from "../GlobalStyle";
import "../fonts.css";
import i18n from "../i18n";
import theme from "../theme";
import { getIsWebView } from "../utils/webView";
import Box from "./Box";
import ElementDialog from "./ElementDialog";
import Footer from "./Footer";
import GameHeader from "./GameHeader";
import { Main, Wrapper } from "./Layout";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useEffect } from "react";
import { fetchMe } from "../utils/MsalUtils";
import Routes from "./Routes";
import ServerError from "./ServerError";
import AutoJoinDialog from "./leagues/AutoJoinDialog";

interface IProps {
  bootstrapped: boolean;
  player: ReturnType<typeof getPlayerData>;
  serverError: IServerError | null;
}

export const Loading: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Wrapper>
      <Main>
        <Box mx={2}>{t("loading", "Loading")} ...</Box>
      </Main>
    </Wrapper>
  );
};

const App: React.FC<IProps> = ({ bootstrapped, player, serverError }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isFullyLoggedOut =
    !isAuthenticated && inProgress === InteractionStatus.None;
  const queryStringParams = getIsWebView() ? "?webview=1" : "";

  // Handle scroll restoration
  useScrollToTop();

  interface LogoutOptions {
    postLogoutRedirectUri: string;
  }
  const options_miss: LogoutOptions = {
    postLogoutRedirectUri: "/sso-missing-details" + queryStringParams,
  };
  const options: LogoutOptions = {
    postLogoutRedirectUri: "/sso-error" + queryStringParams,
  };

  if (getIsWebView() && isAuthenticated) {
    // assume is embedded in the app, so no need for OneID
    const fetchBody = async () => {
      try {
        const body = await fetchMe({}, {});
        if (!body.player) {
          window.location.replace(`/player/logout${queryStringParams}`);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        instance.logoutRedirect(options);
      }
    };
    fetchBody();
  } else if (!getIsWebView()) {
    useEffect(() => {
      if (!getIsWebView() && isAuthenticated && bootstrapped) {
        const idToken = async function getToken() {
          if (!accounts || accounts.length === 0) return null;

          const request = {
            scopes: ["openid"],
            account: accounts[0],
          };

          try {
            const response = await instance.acquireTokenSilent(request);
            return response.idToken; // Return the ID token
          } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
              // fallback if silent fails
              return instance.acquireTokenRedirect(request);
            }
            throw error;
          }
        };

        async function tokenPromise() {
          const idTokenPromise = idToken();
          const idTokenValue = await idTokenPromise;

          if (!idTokenValue) {
            instance.logoutRedirect(options);
            return;
          }
          return idTokenValue;
        }
        const idTokenPromise = tokenPromise();
        const idTokenValue =
          accounts && accounts[0] && accounts[0]?.idTokenClaims?.exp;
        if (!idTokenValue) {
          instance.logoutRedirect(options);
          return;
        }
        // must have first and last name to continue
        if (
          (accounts &&
            accounts[0] &&
            !accounts[0]?.idTokenClaims?.given_name) ||
          (accounts && accounts[0] && !accounts[0]?.idTokenClaims?.family_name)
        ) {
          // redirect to SSO missing details page
          instance.logoutRedirect(options_miss);
        }
        const fetchBody = async () => {
          const token = await idTokenPromise;
          const headers = { Authorization: token };

          const body = await fetchMe({}, headers);
          if (body.error) {
            console.error("Error fetching player data:", body.error);
            instance.logoutRedirect(options);
            return;
          }
          if (
            accounts &&
            accounts[0] &&
            body.player &&
            body.player.lpid !== accounts[0]?.idTokenClaims?.sub
          ) {
            window.location.replace(`/player/logout${queryStringParams}`);
          } else if (
            !window.location.pathname.includes("player/logout") &&
            !isAuthenticated &&
            body.player
          ) {
            window.location.replace(`/player/logout/${queryStringParams}`);
          } else if (isFullyLoggedOut && body.player) {
            window.location.replace(`/player/logout/${queryStringParams}`);
          } else if (!body.player || !player) {
            // Either new user (no player yet) or existing user whose session
            // was just established by the middleware but Redux hasn't caught up
            // yet. Redirect so the app re-bootstraps with the session cookie.
            window.location.replace(`/player/logged-in${queryStringParams}`);
          }
        };

        fetchBody();
      }
    }, [
      instance,
      accounts,
      inProgress,
      isAuthenticated,
      bootstrapped,
      queryStringParams,
    ]);
  }
  return (
    <React.StrictMode>
      <GlobalStyle />
      <ThemeProvider theme={theme}>
        <React.Suspense fallback={<div />}>
          <Helmet>
            <html lang={i18n.language} />
            <title>
              {i18n.t("app.gameName", "Fantasy Liga Portugal Betclic")}
            </title>
            <meta
              name="description"
              content={i18n.t<string>(
                "app.description1n",
                "The new Fantasy Liga Portugal Betclic game for the 2026/27 season.",
              )}
            />
          </Helmet>
          <div className="ism">
            <GameHeader />
            {serverError ? (
              <ServerError error={serverError} />
            ) : bootstrapped ? (
              <Routes />
            ) : (
              <Loading />
            )}
            {!getIsWebView() && <Footer />}
            <AutoJoinDialog />
            <ElementDialog />
          </div>
        </React.Suspense>
      </ThemeProvider>
    </React.StrictMode>
  );
};

interface IContainerPropsFromState {
  bootstrapped: boolean;
  player: ReturnType<typeof getPlayerData>;
  serverError: IServerError | null;
}

interface IState {
  eventId: string | null;
  error: Error | null;
}

interface IContainerPropsFromDispatch {
  bootstrapApp: () => Promise<void>;
}
type ContainerProps = IContainerPropsFromState & IContainerPropsFromDispatch;
class AppContainer extends React.Component<ContainerProps, IState> {
  public state: IState = { error: null, eventId: null };

  public componentDidMount() {
    this.props.bootstrapApp();
  }

  public componentDidCatch(error: Error | null, errorInfo: {}) {
    this.setState({ error });
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
      Sentry.withScope((scope) => {
        scope.setExtras(errorInfo);
        const eventId = Sentry.captureException(error);
        this.setState({ eventId });
      });
    }
  }

  public render() {
    // JS Error
    if (this.state.error) {
      // Can't reliably use any of our components, eg Button. This should
      //probably be in Norwegian can't use react to do it though ...
      return (
        <>
          <h2>Error</h2>
          <p>We're sorry - something's gone wrong.</p>
          {import.meta.env.VITE_SENTRY_ENVIRONMENT && (
            <>
              <p>
                Our team has been notified, but click here to fill out a report.
              </p>
              <button
                onClick={() =>
                  Sentry.showReportDialog({
                    eventId: this.state.eventId as string,
                  })
                }
              >
                Report feedback
              </button>
            </>
          )}
        </>
      );
    }
    // All OK - Render App
    return (
      <App
        bootstrapped={this.props.bootstrapped}
        player={this.props.player}
        serverError={this.props.serverError}
      />
    );
  }
}

const mapStateToProps = (state: RootState): IContainerPropsFromState => ({
  bootstrapped: getBootstrapped(state),
  player: getPlayerData(state),
  serverError: getServerError(state),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch,
): IContainerPropsFromDispatch => ({
  bootstrapApp: () => dispatch(bootstrap()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AppContainer);
